import React from "react";
import { useState, useEffect } from "react";
import "@/App.css";
import axios from "axios";
import { Calendar } from "./components/ui/calendar";
import { de } from "date-fns/locale";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from "./components/ui/accordion";
import { Toaster, toast } from "sonner";
import { BrowserRouter, Routes, Route, Link, useNavigate } from "react-router-dom";
import {
  Phone,
  Mail,
  MapPin,
  FileText,
  Download,
  Menu,
  X,
  ChevronDown,
  Calendar as CalendarIcon,
  Clock,
  Users,
  TreeDeciduous,
  Sun,
  Quote,
  Send,
  ChevronUp,
  ArrowLeft,
  Plus,
  Trash2,
  Edit,
  Save,
  LogIn,
  LogOut,
  Truck,
  Printer
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Logo URL - lokal
const LOGO_URL = "/logo.gif";

// Lokale Bilder
const IMAGES = {
  hero: "/images/hero.jpg",
  heroFruehling: "/images/hero-fruehling.jpg",
  heroSommer: "/images/hero-sommer.jpg",
  heroHerbst: "/images/hero-herbst.jpg",
  heroWinter: "/images/hero-winter.jpg",
  community: "/images/community.jpg",
  clubhouse: "/images/vereinshaus.jpg",
  tent: "/images/zelt.jpg",
  kitchen: "/images/kueche.jpg",
  room: "/images/hauptraum.jpg",
  wc: "/images/toilette.jpg"
};

// Vorstand data - Geschäftsführender Vorstand
const VORSTAND = [
  { funktion: "Vorsitzende", name: "Stefanie Wiedow", email: "info@gosewinkel-ev.de" },
  { funktion: "Stellv. Vorsitzender", name: "Torsten Prohaska", email: "info@gosewinkel-ev.de" },
  { funktion: "Finanzverwalterin", name: "Dora Konrad", email: "schatzmeister@gosewinkel-ev.de" },
  { funktion: "Schriftführerin", name: "Franka Meinhardt", email: "info@gosewinkel-ev.de" },
  { funktion: "Öffentlichkeitsarbeit", name: "Philip Michalski", email: "newsletter@gosewinkel-ev.de" }
];

// Erweiterter Vorstand
const ERWEITERTER_VORSTAND = [
  { funktion: "Wertermittler", name: "Vakant", email: "info@gosewinkel-ev.de" },
  { funktion: "Wasserwart", name: "Vakant", email: "info@gosewinkel-ev.de" },
  { funktion: "Elektrowart", name: "Vakant", email: "info@gosewinkel-ev.de" },
  { funktion: "Fachberater", name: "Vakant", email: "info@gosewinkel-ev.de" }
];

// Documents - lokal gespeichert
const DOCUMENTS = [
  { title: "Kleingartenordnung", url: "/documents/kleingartenordnung.pdf", description: "Regeln und Vorschriften für unsere Anlage" },
  { title: "Satzung 2022", url: "/documents/satzung-2022.pdf", description: "Vereinssatzung mit allen wichtigen Bestimmungen" },
  { title: "Kündigung", url: "/documents/kuendigung.pdf", description: "Formular zur Kündigung der Mitgliedschaft" },
  { title: "Aufnahmeantrag", url: "/documents/Aufnahmeantrag.pdf", description: "Antrag für neue Mitglieder" }
];

// Testimonials
const TESTIMONIALS = [
  { text: "Unsere Haustiere im Garten. Ich finde diese großen Schnecken super schön und sammle sie gerne zu kleinen Gruppen zusammen.", author: "Pächterin" },
  { text: "Eine Gartenanlage mit Individualismus… So muss es sein", author: "Pächter" },
  { text: "Wohlfühlen und entspannen.", author: "Pächterin" },
  { text: "Eine Gartenanlage, in der die Natur noch Natur ist.", author: "Pächter" },
  { text: "Mein Verein. Super!", author: "Pächter" }
];

// FAQ data
const FAQ_DATA = [
  {
    question: "Gibt es Parkplätze in der Nähe?",
    answer: "Es gibt Parkplätze in der Nähe. Ebenfalls bieten wir, sofern vorhanden, gegen eine kleine Gebühr sowie selbstständige Organisation der Pflege personengebundene Parkplätze an."
  },
  {
    question: "Wie hoch ist die Jahresgebühr?",
    answer: "Die Jahresgebühr beträgt im Moment für ordentliche Mitglieder 70€. Für außerordentliche Mitglieder fallen 10€ pro Jahr an. Dazu kommt der eigene Wasser- und Stromverbrauch sowie die Pacht in Abhängigkeit zur Parzellengröße."
  },
  {
    question: "Kann man jederzeit vorbeikommen?",
    answer: "Die Gartenanlage steht 365 Tage im Jahr für jeden offen. Jeden 1. Samstag im Monat, innerhalb der Gartensaison, ist die Sprechstunde beim Vorstand am Vereinshaus von 10 bis 11 Uhr."
  },
  {
    question: "Wer kann Mitglied werden?",
    answer: "Jeder Schweriner Bürger kann Mitglied in unserem Kleingartenverein werden."
  },
  {
    question: "Wie wird man Parzellenpächter?",
    answer: "Sende uns deine Bewerbung zu. Du wirst in unsere Bewerberliste aufgenommen. Wird eine Gartenparzelle frei, z. B. bei Wegzug eines Pächters, wird diese durch einen Wertermittler vom Kreisverband bewertet und zu dem ermittelten Schätzpreis dem Nächsten auf der Bewerberliste angeboten."
  },
  {
    question: "Was sind die zu erwartenden Kosten?",
    answer: "Mitgliedsbeitrag, Pachtgebühr, Versicherungsbeiträge, Wasserumlagen etc. werden, soweit nicht durch übergeordnete Stellen vorgegeben, in der Mitgliederversammlung festgelegt. Weitere Kosten ergeben sich bei Übernahme eines Gartens (Ablöse des Gartenhäuschens, der Pflanzen, Bäume, Sträucher etc.) jeweils nach Schätzung eines Wertermittlers vom Kreisverband sowie durch laufende Betriebskosten wie z.B. für Wasser- und Stromverbrauch."
  }
];

// Saison-Informationen für saisonales Hero-Bild
const getSeasonInfo = () => {
  const month = new Date().getMonth() + 1; // 1-12
  if (month >= 3 && month <= 5) return {
    season: 'Frühling',
    image: IMAGES.heroFruehling,
    gradient: 'linear-gradient(to bottom, rgba(34, 85, 40, 0.5), rgba(60, 120, 60, 0.2), #F2EFE9)',
    badgeColor: 'rgba(34, 197, 94, 0.2)'
  };
  if (month >= 6 && month <= 8) return {
    season: 'Sommer',
    image: IMAGES.heroSommer,
    gradient: 'linear-gradient(to bottom, rgba(100, 70, 10, 0.5), rgba(160, 110, 20, 0.2), #F2EFE9)',
    badgeColor: 'rgba(234, 179, 8, 0.25)'
  };
  if (month >= 9 && month <= 11) return {
    season: 'Herbst',
    image: IMAGES.heroHerbst,
    gradient: 'linear-gradient(to bottom, rgba(110, 45, 10, 0.55), rgba(160, 85, 20, 0.25), #F2EFE9)',
    badgeColor: 'rgba(249, 115, 22, 0.25)'
  };
  return {
    season: 'Winter',
    image: IMAGES.heroWinter,
    gradient: 'linear-gradient(to bottom, rgba(25, 45, 90, 0.6), rgba(45, 70, 120, 0.3), #F2EFE9)',
    badgeColor: 'rgba(147, 197, 253, 0.2)'
  };
};

// Navigation component
const Navigation = ({ activeSection }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { id: "start", label: "Start" },
    { id: "verein", label: "Verein" },
    { id: "dokumente", label: "Dokumente" },
    { id: "mitgliedschaft", label: "Mitgliedschaft" },
    { id: "vereinsheim", label: "Vereinsheim" },
    { id: "termine", label: "Termine" },
    { id: "kontakt", label: "Kontakt" }
  ];

  const scrollToSection = (id) => {
    if (window.location.pathname !== '/') {
      window.location.href = `/#${id}`;
      return;
    }
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsOpen(false);
  };

  return (
    <nav
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'glass shadow-lg' : 'bg-white/90 backdrop-blur-sm shadow-sm'
      }`}
      role="navigation"
      aria-label="Hauptnavigation"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <a
            href="#start"
            onClick={(e) => { e.preventDefault(); scrollToSection('start'); }}
            className="flex items-center gap-3"
            data-testid="logo-link"
            aria-label="Zur Startseite"
          >
            <img src={LOGO_URL} alt="Logo Kleingartenverein Gosewinkel e.V. - Rote Rose auf grünem Kreis, gegründet 1936" className="h-14 w-14" />
            <span className="font-heading text-lg text-forest hidden sm:block">Gosewinkel e.V.</span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8" role="menubar">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                role="menuitem"
                className={`nav-link text-sm font-medium transition-colors ${
                  activeSection === item.id ? 'text-forest' : 'text-text-secondary hover:text-forest'
                }`}
                data-testid={`nav-${item.id}`}
              >
                {item.label}
              </button>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 bg-forest text-white rounded-lg shadow-md"
            data-testid="mobile-menu-toggle"
            aria-label="Menü öffnen"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu - Full Screen Overlay */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="md:hidden fixed inset-0 bg-black/50 z-40"
            onClick={() => setIsOpen(false)}
          />
          {/* Menu */}
          <div
            className="md:hidden fixed inset-x-4 top-24 bg-forest rounded-2xl shadow-2xl z-50 overflow-hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col py-6">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className="text-xl text-white hover:bg-forest-hover transition-colors py-4 px-8 text-left border-b border-white/10 last:border-b-0"
                  data-testid={`mobile-nav-${item.id}`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  const currentMonth = new Date().toLocaleString('de-DE', { month: 'long' });
  const [previewSeason, setPreviewSeason] = useState(null);
  const autoSeason = getSeasonInfo();
  const seasonInfo = previewSeason || autoSeason;

  const seasons = [
    { key: 'fruehling', label: 'Frühling', info: { season: 'Frühling', image: IMAGES.heroFruehling, gradient: 'linear-gradient(to bottom, rgba(34, 85, 40, 0.5), rgba(60, 120, 60, 0.2), #F2EFE9)', badgeColor: 'rgba(34, 197, 94, 0.2)' } },
    { key: 'sommer',    label: 'Sommer',   info: { season: 'Sommer',   image: IMAGES.heroSommer,   gradient: 'linear-gradient(to bottom, rgba(100, 70, 10, 0.5), rgba(160, 110, 20, 0.2), #F2EFE9)', badgeColor: 'rgba(234, 179, 8, 0.25)' } },
    { key: 'herbst',    label: 'Herbst',   info: { season: 'Herbst',   image: IMAGES.heroHerbst,   gradient: 'linear-gradient(to bottom, rgba(110, 45, 10, 0.55), rgba(160, 85, 20, 0.25), #F2EFE9)', badgeColor: 'rgba(249, 115, 22, 0.25)' } },
    { key: 'winter',    label: 'Winter',   info: { season: 'Winter',   image: IMAGES.heroWinter,   gradient: 'linear-gradient(to bottom, rgba(25, 45, 90, 0.6), rgba(45, 70, 120, 0.3), #F2EFE9)', badgeColor: 'rgba(147, 197, 253, 0.2)' } },
  ];

  return (
    <section
      id="start"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      data-testid="hero-section"
      aria-label="Willkommen beim Kleingartenverein Gosewinkel"
    >
      {/* Background Image - saisonal */}
      <div
        className="absolute inset-0 hero-parallax"
        style={{ backgroundImage: `url(${seasonInfo.image})`, transition: 'background-image 0.8s ease' }}
        role="img"
        aria-label="Gartenanlage – saisonales Bild"
      >
        {/* Dunkler Basis-Overlay für Lesbarkeit */}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.35) 60%, rgba(0,0,0,0.1) 85%, #F2EFE9 100%)' }}></div>
        {/* Saisonaler Farb-Overlay */}
        <div className="absolute inset-0" style={{ background: seasonInfo.gradient, transition: 'background 0.8s ease' }}></div>
      </div>

      {/* Jahreszeiten-Vorschau Schalter – oben rechts */}

      {/* Content */}
      <header className="relative z-10 max-w-4xl mx-auto px-4 text-center pt-20">
        <div
          className="inline-flex items-center gap-2 backdrop-blur-sm rounded-full px-4 py-2 mb-8"
          style={{ backgroundColor: seasonInfo.badgeColor, border: '1px solid rgba(255,255,255,0.2)' }}
        >
          <Sun size={18} className="text-lemon" aria-hidden="true" />
          <span className="text-white text-sm font-medium">{seasonInfo.season} im Garten · {currentMonth}</span>
        </div>

        <h1 className="font-heading text-5xl md:text-7xl text-white mb-6 leading-tight hero-text-shadow">
          Herzlich Willkommen
        </h1>
        <p className="text-xl md:text-2xl text-white/90 mb-4 font-light hero-text-shadow">
          beim Kleingartenverein
        </p>
        <p className="text-3xl md:text-4xl text-white font-heading mb-8 hero-text-shadow">
          Gosewinkel e.V.
        </p>
        <p className="text-lg text-white/90 mb-12 max-w-2xl mx-auto hero-text-shadow">
          Dein Kleingartenverein in Schwerin seit 1936. Hier findest du alle Informationen
          über den Verein, Termine, freie Parzellen und alle aktuellen Dokumente.
        </p>

        <nav className="flex flex-col sm:flex-row gap-4 justify-center" aria-label="Schnellnavigation">
          <a
            href="#mitgliedschaft"
            className="btn-primary inline-flex items-center justify-center gap-2"
            data-testid="hero-cta-mitglied"
          >
            Mitglied werden
            <ChevronDown size={18} aria-hidden="true" />
          </a>
          <a
            href="#termine"
            className="btn-primary inline-flex items-center justify-center gap-2"
            data-testid="hero-cta-termine"
          >
            <CalendarIcon size={18} aria-hidden="true" />
            Termine ansehen
              <ChevronDown size={18} aria-hidden="true" />
          </a>
        </nav>
      </header>

      {/* Scroll indicator – außerhalb des Headers, relativ zur Section zentriert */}
      <div className="absolute bottom-10 left-0 right-0 flex justify-center animate-bounce z-10" aria-hidden="true">
        <ChevronDown size={32} className="text-white/60" />
      </div>
    </section>
  );
};

// Stats Section
const StatsSection = () => {
  const stats = [
    { number: 280, label: "Parzellen", icon: <TreeDeciduous size={32} /> },
    { number: 200, label: "Mitglieder", icon: <Users size={32} /> },
    { number: 365, label: "Tage Natur", icon: <Sun size={32} /> }
  ];

  return (
    <section className="py-20 bg-bone-paper" data-testid="stats-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {stats.map((stat, index) => (
            <div
              key={index}
              className="card-organic text-center p-8 group"
              data-testid={`stat-card-${index}`}
            >
              <div className="text-forest/20 mb-4 flex justify-center group-hover:text-forest/40 transition-colors">
                {stat.icon}
              </div>
              <div className="text-6xl font-heading text-forest mb-2 stat-number">
                {stat.number}
              </div>
              <div className="text-text-secondary font-medium uppercase tracking-wider text-sm">
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Vorstand Section
const VorstandSection = () => {
  const [showExtended, setShowExtended] = useState(false);

  return (
    <section id="verein" className="py-24 bg-bone" data-testid="vorstand-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="font-heading text-4xl md:text-5xl text-forest mb-4">
            Unser Vorstand
          </h2>
          <p className="text-text-secondary max-w-2xl mx-auto">
            Wir freuen uns, dich an dieser Stelle näher mit den Mitgliedern
            unseres Vereinsvorstandes bekannt zu machen.
          </p>
        </div>

        {/* Sprechzeiten Banner */}
        <div className="bg-forest text-white rounded-2xl p-6 mb-12 flex flex-col md:flex-row items-center justify-between gap-4" data-testid="sprechzeiten-banner">
          <div className="flex items-center gap-4">
            <Clock size={24} />
            <div>
              <p className="font-medium">Sprechzeiten des Vorstandes</p>
              <p className="text-white text-sm">Jeden ersten Samstag im Monat, 10 bis 11 Uhr am Vereinshaus</p>
            </div>
          </div>
          <a href="tel:01757572764" className="flex items-center gap-2 bg-white/25 rounded-full px-6 py-2 hover:bg-white/35 transition-colors text-white font-medium">
            <Phone size={18} />
            0175 7572764
          </a>
        </div>

        {/* Geschäftsführender Vorstand */}
        <h3 className="font-heading text-2xl text-forest mb-6">Geschäftsführender Vorstand</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {VORSTAND.map((member, index) => (
            <div
              key={index}
              className="bg-bone-paper p-6 rounded-xl border-l-4 border-forest hover:shadow-md transition-all"
              data-testid={`vorstand-card-${index}`}
            >
              <p className="text-forest font-medium text-sm uppercase tracking-wider mb-1">
                {member.funktion}
              </p>
              <p className="font-heading text-xl text-text-primary mb-4">
                {member.name}
              </p>
              <a
                href={`mailto:${member.email}`}
                className="inline-flex items-center gap-2 text-forest hover:text-forest-hover transition-colors text-sm"
              >
                <Mail size={16} />
                {member.email}
              </a>
            </div>
          ))}
        </div>

        {/* Toggle Button für erweiterten Vorstand */}
        <button
          onClick={() => setShowExtended(!showExtended)}
          className="btn-secondary flex items-center gap-2 mx-auto mb-8"
          data-testid="toggle-extended-vorstand"
        >
          {showExtended ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          {showExtended ? 'Erweiterten Vorstand ausblenden' : 'Erweiterten Vorstand anzeigen'}
        </button>

        {/* Erweiterter Vorstand */}
        {showExtended && (
          <div className="animate-fade-in">
            <h3 className="font-heading text-2xl text-forest mb-6">Erweiterter Vorstand</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {ERWEITERTER_VORSTAND.map((member, index) => (
                <div
                  key={index}
                  className="bg-bone-paper p-6 rounded-xl border-l-4 border-rose hover:shadow-md transition-all"
                  data-testid={`erweiterter-vorstand-card-${index}`}
                >
                  <p className="text-rose font-medium text-sm uppercase tracking-wider mb-1">
                    {member.funktion}
                  </p>
                  <p className="font-heading text-xl text-text-primary mb-4">
                    {member.name}
                  </p>
                  <a
                    href={`mailto:${member.email}`}
                    className="inline-flex items-center gap-2 text-forest hover:text-forest-hover transition-colors text-sm"
                  >
                    <Mail size={16} />
                    {member.email}
                  </a>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

// Documents Section
const DocumentsSection = () => {
  return (
    <section id="dokumente" className="py-24 bg-bone-paper" data-testid="documents-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="font-heading text-4xl md:text-5xl text-forest mb-4">
            Vereinsdokumente
          </h2>
          <p className="text-text-secondary">
            Hier findest du unsere wichtigen Vereinsdokumente zum Download
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {DOCUMENTS.map((doc, index) => (
            <a
              key={index}
              href={doc.url}
              target="_blank"
              rel="noopener noreferrer"
              className="doc-card bg-white p-6 rounded-xl border border-forest/10 flex flex-col items-center text-center group"
              data-testid={`document-card-${index}`}
            >
              <div className="w-16 h-16 bg-forest/10 rounded-full flex items-center justify-center mb-4 group-hover:bg-forest/20 transition-colors">
                <FileText size={28} className="text-forest" />
              </div>
              <p className="font-medium text-text-primary mb-2">{doc.title}</p>
              <p className="text-xs text-text-muted mb-3">{doc.description}</p>
              <span className="text-sm text-forest flex items-center gap-1 font-medium">
                <Download size={14} />
                PDF herunterladen
              </span>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

// Membership Section
const MembershipSection = () => {
  return (
    <section id="mitgliedschaft" className="py-24 bg-bone" data-testid="membership-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          <div>
            <h2 className="font-heading text-4xl md:text-5xl text-forest mb-4">
              Mitglied werden
            </h2>
            <p className="text-text-secondary mb-8">
              Erfülle dir den Traum eines eigenen Gartens. Die Mitglieder können
              an Hauptversammlungen und Veranstaltungen des Vereins teilnehmen, dort ihr
              Stimmrecht ausüben und sich in Vereinsangelegenheiten an den Vorstand wenden.
            </p>

            {/* Bild */}
            <div className="relative rounded-2xl overflow-hidden mb-8">
              <img
                src={IMAGES.community}
                alt="Gartengemeinschaft"
                className="w-full h-64 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-forest/60 to-transparent"></div>
              <div className="absolute bottom-4 left-4 text-white">
                <p className="font-heading text-xl">Ihr Traumgarten wartet</p>
              </div>
            </div>

            <a
              href="/documents/Aufnahmeantrag.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary inline-flex items-center gap-2"
              data-testid="membership-cta"
            >
              <Download size={18} />
              Aufnahmeantrag herunterladen
            </a>
          </div>

          <div>
            <h3 className="font-heading text-2xl text-forest mb-6">
              Häufig gestellte Fragen
            </h3>
            <Accordion type="single" collapsible className="space-y-4" data-testid="faq-accordion">
              {FAQ_DATA.map((item, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="bg-white rounded-xl border border-forest/10 px-6 overflow-hidden"
                >
                  <AccordionTrigger className="text-left font-medium text-text-primary hover:text-forest py-4">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-text-secondary pb-4">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </div>
    </section>
  );
};

// Clubhouse Section
const ClubhouseSection = () => {
  const [selectedImage, setSelectedImage] = useState(IMAGES.clubhouse);

  const galleryImages = [
    { src: IMAGES.clubhouse, label: "Vereinshaus" },
    { src: IMAGES.tent, label: "Vereinszelt" },
    { src: IMAGES.kitchen, label: "Küche" },
    { src: IMAGES.room, label: "Hauptraum" },
    { src: IMAGES.wc, label: "Sanitär" }
  ];

  return (
    <section id="vereinsheim" className="py-24 bg-bone-paper" data-testid="clubhouse-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="font-heading text-4xl md:text-5xl text-forest mb-4">
            Unser Vereinsheim
          </h2>
          <p className="text-text-secondary max-w-2xl mx-auto">
            Unser Vereinshaus ist der Treffpunkt für Versammlungen, Sommerfeste
            und diverse andere Aktivitäten und Feierlichkeiten.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Main Image */}
          <div>
            <div className="rounded-2xl overflow-hidden mb-4 gallery-item">
              <img
                src={selectedImage}
                alt="Vereinsheim"
                className="w-full h-96 object-cover"
              />
            </div>
            {/* Thumbnails */}
            <div className="grid grid-cols-5 gap-2">
              {galleryImages.map((img, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedImage(img.src)}
                  className={`rounded-lg overflow-hidden border-2 transition-all ${
                    selectedImage === img.src ? 'border-forest' : 'border-transparent'
                  }`}
                  data-testid={`gallery-thumb-${index}`}
                >
                  <img
                    src={img.src}
                    alt={img.label}
                    className="w-full h-16 object-cover"
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Info */}
          <div className="space-y-6">
            <div className="card-organic">
              <h3 className="font-heading text-xl text-forest mb-3">
                Vereinshaus mieten
              </h3>
              <p className="text-text-secondary mb-4">
                Das Vereinshaus kann gemietet werden. Bitte wende dich hierfür
                an den Geschäftsführenden Vorstand.
              </p>
              <ul className="space-y-2 text-text-secondary">
                <li className="flex items-center gap-2">
                  <Users size={18} className="text-forest" />
                  Bis zu 20 Personen
                </li>
                <li className="flex items-center gap-2">
                  <MapPin size={18} className="text-forest" />
                  Großzügiger Außenbereich
                </li>
              </ul>
            </div>

            <div className="bg-rose/30 rounded-xl p-6">
              <p className="text-text-primary font-medium mb-2">
                Mich kann man als Vereinsmitglied mieten!
              </p>
              <p className="text-text-secondary text-sm">
                Sanitäranlagen sind vorhanden und ein großzügiger Außenbereich
                lädt zum Verweilen in der Sonne ein.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Testimonials Section - Karussell
const TestimonialsSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  // Auto-play
  useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % TESTIMONIALS.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  const goToSlide = (index) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
    // Resume auto-play after 10 seconds
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % TESTIMONIALS.length);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + TESTIMONIALS.length) % TESTIMONIALS.length);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  return (
    <section className="py-24 bg-forest" data-testid="testimonials-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="font-heading text-4xl md:text-5xl text-white mb-4">
            Bewertungen
          </h2>
          <p className="text-white/70">Dies sagen unsere Pächter</p>
        </div>

        {/* Karussell */}
        <div className="relative">
          {/* Navigation Arrows */}
          <button
            onClick={prevSlide}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-12 w-10 h-10 md:w-12 md:h-12 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center text-white transition-colors z-10"
            aria-label="Vorherige Bewertung"
          >
            <ChevronDown className="rotate-90" size={24} />
          </button>

          <button
            onClick={nextSlide}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-12 w-10 h-10 md:w-12 md:h-12 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center text-white transition-colors z-10"
            aria-label="Nächste Bewertung"
          >
            <ChevronDown className="-rotate-90" size={24} />
          </button>

          {/* Slide Content */}
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 md:p-12 text-center min-h-[250px] flex flex-col justify-center">
            <Quote size={40} className="text-white/30 mx-auto mb-6" aria-hidden="true" />
            <p className="text-white text-xl md:text-2xl mb-6 leading-relaxed">
              "{TESTIMONIALS[currentIndex].text}"
            </p>
            <p className="text-white/90">— {TESTIMONIALS[currentIndex].author}</p>
          </div>

          {/* Dots Navigation */}
          <div className="flex justify-center gap-2 mt-8">
            {TESTIMONIALS.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-3 h-3 rounded-full transition-all ${
                  index === currentIndex
                    ? 'bg-white w-8'
                    : 'bg-white/40 hover:bg-white/60'
                }`}
                aria-label={`Bewertung ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Calendar/Events Section
const EventsSection = () => {
  const [events, setEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/events`);
      setEvents(response.data);
    } catch (error) {
      console.error("Error fetching events:", error);
      // Use default events if API fails
      setEvents([
        {
          id: "1",
          title: "Vorstandssprechstunde",
          description: "Jeden ersten Samstag im Monat von 10 bis 11 Uhr am Vereinshaus.",
          date: "2025-01-04",
          time: "10:00 - 11:00",
          location: "Vereinshaus",
          event_type: "sprechstunde"
        },
        {
          id: "2",
          title: "Vorstandssprechstunde",
          description: "Jeden ersten Samstag im Monat von 10 bis 11 Uhr am Vereinshaus.",
          date: "2025-02-01",
          time: "10:00 - 11:00",
          location: "Vereinshaus",
          event_type: "sprechstunde"
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  // Get dates with events for calendar highlighting
  const eventDates = events.map(e => new Date(e.date));

  // Filter events for selected month
  const filteredEvents = events.filter(event => {
    const eventDate = new Date(event.date);
    return eventDate.getMonth() === selectedDate.getMonth() &&
           eventDate.getFullYear() === selectedDate.getFullYear();
  }).sort((a, b) => new Date(a.date) - new Date(b.date))
    .filter((event, index, self) =>
      index === self.findIndex(e => e.id === event.id)
    );

  const getEventTypeColor = (type) => {
    switch(type) {
      case 'sprechstunde': return 'bg-forest';
      case 'fest': return 'bg-rose';
      case 'versammlung': return 'bg-earth';
      default: return 'bg-lemon';
    }
  };

  return (
    <section id="termine" className="py-24 bg-bone" data-testid="events-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="font-heading text-4xl md:text-5xl text-forest mb-4">
            Termine & Veranstaltungen
          </h2>
          <p className="text-text-secondary">
            Bleib auf dem Laufenden über unsere Veranstaltungen
          </p>
        </div>

        {/* Termin-Kategorien */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <Link
            to="/termine/verein"
            className="card-organic p-6 text-center hover:shadow-lg transition-all group"
          >
            <div className="w-16 h-16 bg-forest/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-forest/20 transition-colors">
              <CalendarIcon size={28} className="text-forest" />
            </div>
            <h3 className="font-heading text-xl text-forest mb-2">Vereinstermine</h3>
            <p className="text-text-secondary text-sm">Versammlungen, Feste, Arbeitseinsätze</p>
          </Link>

          <Link
            to="/termine/faekalien"
            className="card-organic p-6 text-center hover:shadow-lg transition-all group"
          >
            <div className="w-16 h-16 bg-forest/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-forest/20 transition-colors">
              <Truck size={28} className="text-forest" />
            </div>
            <h3 className="font-heading text-xl text-forest mb-2">Fäkalienabfuhr</h3>
            <p className="text-text-secondary text-sm">Abfuhrtermine für das aktuelle Jahr</p>
          </Link>

          <Link
            to="/termine/sonstige"
            className="card-organic p-6 text-center hover:shadow-lg transition-all group"
          >
            <div className="w-16 h-16 bg-forest/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-forest/20 transition-colors">
              <Users size={28} className="text-forest" />
            </div>
            <h3 className="font-heading text-xl text-forest mb-2">Sonstige Termine</h3>
            <p className="text-text-secondary text-sm">Skatturnier, Stammtisch & mehr</p>
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Calendar */}
          <div className="card-organic p-6 flex justify-center" data-testid="events-calendar">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => date && setSelectedDate(date)}
              onMonthChange={(month) => setSelectedDate(month)}
              locale={de}
              modifiers={{
                hasEvent: eventDates
              }}
              modifiersStyles={{
                hasEvent: {
                  backgroundColor: '#2F5233',
                  color: 'white',
                  borderRadius: '50%'
                }
              }}
              className="rounded-lg"
            />
          </div>

          {/* Events List */}
          <div>
            <h3 className="font-heading text-xl text-forest mb-6">
              Kommende Termine
            </h3>
            {loading ? (
              <p className="text-text-muted">Termine werden geladen...</p>
            ) : filteredEvents.length > 0 ? (
              <div className="space-y-4">
                {filteredEvents.map((event) => (
                  <div
                    key={event.id}
                    className="bg-white rounded-xl p-6 border border-forest/10 hover:border-forest/30 transition-all"
                    data-testid={`event-item-${event.id}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`${getEventTypeColor(event.event_type)} text-white rounded-lg p-3 text-center min-w-[60px]`}>
                        <div className="text-2xl font-bold">
                          {new Date(event.date).getDate()}
                        </div>
                        <div className="text-xs uppercase">
                          {new Date(event.date).toLocaleString('de-DE', { month: 'short' })}
                        </div>
                      </div>
                      <div>
                        <h4 className="font-medium text-text-primary">{event.title}</h4>
                        <p className="text-text-secondary text-sm mb-2">{event.description}</p>
                        <div className="flex flex-wrap gap-4 text-sm text-text-muted">
                          {event.time && (
                            <span className="flex items-center gap-1">
                              <Clock size={14} />
                              {event.time}
                            </span>
                          )}
                          {event.location && (
                            <span className="flex items-center gap-1">
                              <MapPin size={14} />
                              {event.location}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-text-muted">Keine Termine in diesem Monat.</p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Bitte fülle alle Felder aus.");
      return;
    }

    setSending(true);
    try {
      const response = await axios.post(`${API}/contact`, formData);
      toast.success(response.data.message);
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Fehler beim Senden. Bitte versuche es später erneut.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="kontakt" className="py-24 bg-bone-paper" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Contact Info */}
          <div>
            <h2 className="font-heading text-4xl md:text-5xl text-forest mb-4">
              Kontakt
            </h2>
            <p className="text-text-secondary mb-8">
              Hinterlasse uns eine Nachricht. Der Vorstand versucht,
              dir so schnell wie möglich zu antworten.
            </p>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-forest/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <MapPin className="text-forest" size={20} />
                </div>
                <div>
                  <p className="font-medium text-text-primary">Adresse</p>
                  <p className="text-text-secondary">
                    Kleingartenverein Gosewinkel e.V.<br />
                    Schwerin
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-forest/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Phone className="text-forest" size={20} />
                </div>
                <div>
                  <p className="font-medium text-text-primary">Telefon</p>
                  <a href="tel:01757572764" className="text-forest hover:text-forest-hover transition-colors">
                    0175 7572764
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-forest/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Mail className="text-forest" size={20} />
                </div>
                <div>
                  <p className="font-medium text-text-primary">E-Mail</p>
                  <a href="mailto:info@gosewinkel-ev.de" className="text-forest hover:text-forest-hover transition-colors">
                    info@gosewinkel-ev.de
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="card-organic p-8">
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="input-underline bg-bone-paper"
                  placeholder="Ihr Name"
                  required
                  data-testid="contact-name-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  E-Mail *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="input-underline bg-bone-paper"
                  placeholder="ihre@email.de"
                  required
                  data-testid="contact-email-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Nachricht *
                </label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  className="input-underline bg-bone-paper min-h-[150px] resize-none"
                  placeholder="Deine Nachricht an uns..."
                  required
                  data-testid="contact-message-input"
                />
              </div>

              <button
                type="submit"
                disabled={sending}
                className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="contact-submit-btn"
              >
                {sending ? (
                  "Wird gesendet..."
                ) : (
                  <>
                    <Send size={18} />
                    Nachricht senden
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="bg-forest text-white py-12" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="Gosewinkel Logo" className="h-16 w-16" />
            <div>
              <p className="font-heading text-xl">Kleingartenverein Gosewinkel e.V.</p>
              <p className="text-white/85 text-sm">Gegr. 1936 | Schwerin</p>
            </div>
          </div>

          <div className="flex flex-col md:flex-row items-center gap-4 md:gap-8">
            <Link to="/impressum" className="text-white/70 hover:text-white transition-colors text-sm">
              Impressum
            </Link>
            <Link to="/datenschutz" className="text-white/70 hover:text-white transition-colors text-sm">
              Datenschutz
            </Link>
          </div>

          <div className="text-center md:text-right">
            <p className="text-white/85 text-sm">
              © {new Date().getFullYear()} Kleingartenverein Gosewinkel e.V.
            </p>
            <p className="text-white/70 text-xs mt-1">
              Alle Rechte vorbehalten
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Cookie Banner
const CookieBanner = () => {
  const [visible, setVisible] = useState(() => !localStorage.getItem('cookieConsent'));

  const accept = () => {
    localStorage.setItem('cookieConsent', 'accepted');
    setVisible(false);
  };

  const acceptNecessary = () => {
    localStorage.setItem('cookieConsent', 'necessary');
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6"
      role="dialog"
      aria-label="Cookie-Einstellungen"
      data-testid="cookie-banner"
    >
      <div className="max-w-4xl mx-auto bg-forest text-white rounded-2xl shadow-2xl px-6 py-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <div className="flex-1 text-sm leading-relaxed">
          <p className="font-semibold mb-1">Diese Website verwendet Cookies</p>
          <p className="text-white/80 text-xs">
            Wir nutzen ausschließlich technisch notwendige Cookies, um den Betrieb der Website zu gewährleisten.
            Es werden keine Tracking- oder Werbe-Cookies eingesetzt.{' '}
            <Link to="/datenschutz" className="underline hover:text-lemon transition-colors" onClick={acceptNecessary}>
              Datenschutzerklärung
            </Link>
          </p>
        </div>
        <div className="flex gap-3 shrink-0 flex-wrap">
          <button
            onClick={acceptNecessary}
            className="text-xs px-4 py-2 rounded-full border border-white/40 text-white/80 hover:bg-white/10 transition-colors"
            data-testid="cookie-necessary-btn"
          >
            Nur notwendige
          </button>
          <button
            onClick={accept}
            className="text-xs px-4 py-2 rounded-full bg-lemon text-forest font-semibold hover:bg-lemon/90 transition-colors"
            data-testid="cookie-accept-btn"
          >
            Alle akzeptieren
          </button>
        </div>
      </div>
    </div>
  );
};
const ImpressumPage = () => {
  return (
    <div className="min-h-screen bg-bone" data-testid="impressum-page">
      <Navigation activeSection="" />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pt-32">
        <Link to="/" className="inline-flex items-center gap-2 text-forest hover:text-forest-hover mb-8 transition-colors">
          <ArrowLeft size={18} />
          Zurück zur Startseite
        </Link>

        <h1 className="font-heading text-4xl md:text-5xl text-forest mb-8">Impressum</h1>

        <div className="prose prose-lg max-w-none space-y-6">
          <div className="card-organic">
            <p className="font-medium text-lg text-text-primary mb-4">
              Kleingartenverein "Gosewinkel" e.V.
            </p>
            <p className="text-text-secondary">
              Gosewinkler Weg 132<br />
              19059 Schwerin
            </p>
          </div>

          <div className="card-organic">
            <h3 className="font-heading text-xl text-forest mb-3">Vertretungsberechtigter Vorstand</h3>
            <p className="text-text-secondary mb-4">
              Registernummer: VR 237<br />
              Registergericht: Amtsgericht Schwerin
            </p>
            <p className="text-text-secondary">
              <strong>E-Mail:</strong>{' '}
              <a href="mailto:info@gosewinkel-ev.de" className="text-forest hover:text-forest-hover">
                info@gosewinkel-ev.de
              </a>
            </p>
            <p className="text-text-secondary">
              <strong>Website:</strong>{' '}
              <a href="https://gosewinkel-ev.de" className="text-forest hover:text-forest-hover">
                gosewinkel-ev.de
              </a>
            </p>
          </div>

          <div className="card-organic">
            <h3 className="font-heading text-xl text-forest mb-3">Inhaltlich Verantwortlicher</h3>
            <p className="text-text-secondary">
              Gemäß § 55 Abs. 2 RStV und § 10 Absatz 3 MDStV:<br />
              Kleingartenverein "Gosewinkel" e.V. (Anschrift wie oben)
            </p>
          </div>

          <div className="card-organic">
            <h3 className="font-heading text-xl text-forest mb-3">Haftung für Inhalte</h3>
            <p className="text-text-secondary">
              Trotz sorgfältiger inhaltlicher Kontrolle übernehmen wir keine Haftung für die Inhalte
              externer Links. Für den Inhalt der verlinkten Seiten sind ausschließlich deren
              Betreiber verantwortlich.
            </p>
          </div>

        </div>
      </div>
      <Footer />
    </div>
  );
};

// Datenschutz Page
const DatenschutzPage = () => {
  return (
    <div className="min-h-screen bg-bone" data-testid="datenschutz-page">
      <Navigation activeSection="" />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pt-32">
        <Link to="/" className="inline-flex items-center gap-2 text-forest hover:text-forest-hover mb-8 transition-colors">
          <ArrowLeft size={18} />
          Zurück zur Startseite
        </Link>

        <h1 className="font-heading text-4xl md:text-5xl text-forest mb-8">Datenschutzverordnung</h1>

        <div className="prose prose-lg max-w-none space-y-6">
          <p className="text-text-secondary">
            Der Kleingartenverein Gosewinkel e.V verarbeitet bei Aufruf und Nutzung dieser Website
            personenbezogene Daten im Sinne des Art. 4 Nr. 1 Datenschutz-Grundverordnung (DS-GVO).
            Betroffene Personen haben das Recht, nach Art. 13 DS-GVO informiert zu werden.
          </p>

          <div className="card-organic">
            <h3 className="font-heading text-xl text-forest mb-3">
              1. Name und Kontaktdaten des Verantwortlichen
            </h3>
            <p className="text-text-secondary">
              Im Sinne des Art. 4 Nr. 7 DS-GVO ist der Kleingartenverein Gosewinkel e.V:<br /><br />
              <strong>Postanschrift:</strong> Gosewinkler Weg 132, 19059 Schwerin<br />
              <strong>E-Mail:</strong>{' '}
              <a href="mailto:info@gosewinkel-ev.de" className="text-forest hover:text-forest-hover">
                info@gosewinkel-ev.de
              </a><br />
              <strong>Website:</strong>{' '}
              <a href="https://gosewinkel-ev.de" className="text-forest hover:text-forest-hover">
                gosewinkel-ev.de
              </a>
            </p>
          </div>

          <div className="card-organic">
            <h3 className="font-heading text-xl text-forest mb-3">2. Zwecke der Verarbeitung</h3>
            <p className="text-text-secondary">
              Wir betreiben diese Website um Öffentlichkeitsarbeit zu betreiben. Hieran haben wir
              ein berechtigtes Interesse gem. Art. 6 Abs. 1 lit. f DS-GVO. Die bei der bloß
              informatorischen Nutzung unserer Website erhobenen Daten werden technisch benötigt,
              um unsere Website auf deinem Endgerät anzuzeigen und um die Systemsicherheit zu
              gewährleisten.
            </p>
          </div>

          <div className="card-organic">
            <h3 className="font-heading text-xl text-forest mb-3">
              3. Kategorien personenbezogener Daten
            </h3>
            <p className="text-text-secondary mb-4">
              Bei der informatorischen Nutzung unserer Website erheben wir nur die personenbezogenen
              Daten, die Ihr Browser an den Server unserer Website übermittelt:
            </p>
            <ul className="list-disc list-inside text-text-secondary space-y-1">
              <li>IP-Adresse des anfragenden Rechners</li>
              <li>Datum und Uhrzeit des Zugriffs</li>
              <li>Name und URL der abgerufenen Datei</li>
              <li>URL oder Provider von der/dem aus die Datei angefordert wurde</li>
              <li>Verwendeter Browser einschließlich Einstellungen und Betriebssystem</li>
              <li>Zugriffsstatus des Web-Servers</li>
            </ul>
          </div>

          <div className="card-organic">
            <h3 className="font-heading text-xl text-forest mb-3">4. Cookies</h3>
            <p className="text-text-secondary">
              Darüber hinaus werden bei der informatorischen Nutzung unserer Website so genannte
              transiente Cookies auf deinem Rechner gespeichert. Cookies sind kleine Textdateien,
              die auf deinem Endgerät dem von dir verwendeten Browser zugeordnet gespeichert werden.
              Cookies können keine Programme ausführen oder Viren auf deinen Computer übertragen.
              Sie dienen dazu, unser Internetangebot insgesamt benutzerfreundlicher und effektiver
              zu machen.<br /><br />
              Transiente Cookies werden automatisiert gelöscht, wenn du den Browser schließt.
              Du kannst deine Browser-Einstellung entsprechend deinen Wünschen konfigurieren und
              die Annahme von Cookies ablehnen.
            </p>
          </div>

          <div className="card-organic">
            <h3 className="font-heading text-xl text-forest mb-3">
              5. Rechtsgrundlage und Speicherdauer
            </h3>
            <p className="text-text-secondary">
              Rechtsgrundlagen für die Verarbeitung personenbezogener Daten durch uns ist
              Art. 6 Abs. 1 Buchst. f DS-GVO.<br /><br />
              Die bei der informatorischen Nutzung unserer Website erhobenen personenbezogenen
              Daten werden nach einem Tag gelöscht.
            </p>
          </div>

          <div className="card-organic">
            <h3 className="font-heading text-xl text-forest mb-3">
              6. Rechte der betroffenen Person
            </h3>
            <p className="text-text-secondary mb-4">Du hast uns gegenüber das Recht:</p>
            <ul className="list-disc list-inside text-text-secondary space-y-1">
              <li>gemäß Art. 15 DS-GVO Auskunft über deine von uns verarbeiteten personenbezogenen Daten zu verlangen</li>
              <li>gemäß Art. 16 DS-GVO die Berichtigung unrichtiger personenbezogener Daten zu verlangen</li>
              <li>gemäß Art. 17 DS-GVO die Löschung deiner bei uns gespeicherten personenbezogenen Daten zu verlangen</li>
              <li>gemäß Art. 18 DS-GVO die Einschränkung der Verarbeitung deiner personenbezogenen Daten zu verlangen</li>
              <li>gemäß Art. 21 DS-GVO jederzeit Widerspruch gegen die Verarbeitung einzulegen</li>
            </ul>
          </div>

          <div className="card-organic">
            <h3 className="font-heading text-xl text-forest mb-3">7. Aufsichtsbehörde</h3>
            <p className="text-text-secondary">
              Zudem kannst du dich bei der für uns zuständigen Aufsichtsbehörde beschweren:<br /><br />
              Der Landesbeauftragte für Datenschutz und Informationsfreiheit Mecklenburg-Vorpommern<br />
              Lennéstraße 1, 19053 Schwerin<br />
              <a href="https://datenschutz-mv.de" className="text-forest hover:text-forest-hover">
                datenschutz-mv.de
              </a>
            </p>
          </div>

          <div className="card-organic">
            <h3 className="font-heading text-xl text-forest mb-3">8. E-Mail-Sicherheit</h3>
            <p className="text-text-secondary">
              Wenn du uns eine E-Mail sendest, so wird deine E-Mail-Adresse nur für die
              Korrespondenz mit dir verwendet. Wenn du eine E-Mail mit schutzwürdigem Inhalt
              an uns senden willst, so empfehlen wir dringend, diese zu verschlüsseln, um eine
              unbefugte Kenntnisnahme und Verfälschung auf dem Übertragungsweg zu verhindern.
            </p>
          </div>

          <p className="text-text-muted italic">Der Vorstand</p>
        </div>
      </div>
      <Footer />
    </div>
  );
};

// Vereinstermine Page
const VereinsTerminePage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await axios.get(`${API}/events`);
        // Filter for vereinstermine (versammlung, fest, general but not skatturnier/stammtisch)
        const vereinsEvents = response.data.filter(e =>
          ['versammlung', 'fest', 'sprechstunde'].includes(e.event_type) ||
          (e.event_type === 'general' && !e.title.toLowerCase().includes('skat') && !e.title.toLowerCase().includes('stammtisch'))
        ).sort((a, b) => new Date(a.date) - new Date(b.date));
        setEvents(vereinsEvents);
      } catch (error) {
        console.error("Error fetching events:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, []);

  return (
    <div className="min-h-screen bg-bone" data-testid="vereinstermine-page">
      <Navigation activeSection="termine" />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pt-32">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <Link to="/#termine" className="inline-flex items-center gap-2 text-forest hover:text-forest-hover transition-colors print:hidden">
            <ArrowLeft size={18} />
            Zurück zur Übersicht
          </Link>
          <button
            onClick={() => window.print()}
            className="inline-flex items-center gap-2 bg-forest text-white px-4 py-2 rounded-lg hover:bg-forest-hover transition-colors text-sm print:hidden"
            data-testid="print-verein-btn"
          >
            <Printer size={16} />
            Drucken / Exportieren
          </button>
        </div>

        <h1 className="font-heading text-4xl md:text-5xl text-forest mb-4">Vereinstermine 2026</h1>
        <p className="text-text-secondary mb-8">Versammlungen, Feste und Arbeitseinsätze</p>

        {loading ? (
          <p className="text-text-muted">Termine werden geladen...</p>
        ) : events.length > 0 ? (
          <div className="space-y-4">
            {events.map((event) => (
              <div key={event.id} className="card-organic p-6">
                <div className="flex items-start gap-4">
                  <div className="bg-forest text-white rounded-lg p-3 text-center min-w-[70px]">
                    <div className="text-2xl font-bold">
                      {new Date(event.date).getDate()}
                    </div>
                    <div className="text-xs uppercase">
                      {new Date(event.date).toLocaleString('de-DE', { month: 'short' })}
                    </div>
                  </div>
                  <div>
                    <h3 className="font-heading text-xl text-text-primary mb-1">{event.title}</h3>
                    <p className="text-text-secondary mb-2">{event.description}</p>
                    <div className="flex flex-wrap gap-4 text-sm text-text-muted">
                      {event.time && (
                        <span className="flex items-center gap-1">
                          <Clock size={14} />
                          {event.time}
                        </span>
                      )}
                      {event.location && (
                        <span className="flex items-center gap-1">
                          <MapPin size={14} />
                          {event.location}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-text-muted">Keine Vereinstermine vorhanden.</p>
        )}
      </div>
      <Footer />
    </div>
  );
};

// Fäkalienabfuhr Page
const FaekalienTerminePage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFaekalien = async () => {
      try {
        const response = await axios.get(`${API}/events`);
        const faekalienEvents = response.data
          .filter(e => e.event_type === 'faekalien')
          .sort((a, b) => new Date(a.date) - new Date(b.date));
        setEvents(faekalienEvents);
      } catch (error) {
        console.error("Error fetching events:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchFaekalien();
  }, []);

  return (
    <div className="min-h-screen bg-bone" data-testid="faekalien-page">
      <Navigation activeSection="termine" />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pt-32">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <Link to="/#termine" className="inline-flex items-center gap-2 text-forest hover:text-forest-hover transition-colors print:hidden">
            <ArrowLeft size={18} />
            Zurück zur Übersicht
          </Link>
          <button
            onClick={() => window.print()}
            className="inline-flex items-center gap-2 bg-forest text-white px-4 py-2 rounded-lg hover:bg-forest-hover transition-colors text-sm print:hidden"
            data-testid="print-faekalien-btn"
          >
            <Printer size={16} />
            Drucken / Exportieren
          </button>
        </div>

        <h1 className="font-heading text-4xl md:text-5xl text-forest mb-4">Fäkalienabfuhr 2026</h1>
        <p className="text-text-secondary mb-8">Abfuhrtermine für das Jahr 2026</p>

        {loading ? (
          <p className="text-text-muted">Termine werden geladen...</p>
        ) : events.length === 0 ? (
          <div className="card-organic p-6 text-center text-text-muted">
            <Truck size={40} className="mx-auto mb-3 opacity-30" />
            <p>Keine Fäkalienabfuhr-Termine sind bisher eingetragen.</p>
          </div>
        ) : (
          <div className="card-organic overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-forest text-white">
                  <tr>
                    <th className="px-4 py-3 text-left">Wochentag</th>
                    <th className="px-4 py-3 text-left">Datum</th>
                    <th className="px-4 py-3 text-left">Beginn</th>
                    <th className="px-4 py-3 text-left">Ende</th>
                    <th className="px-4 py-3 text-left">Anzahl Gruben</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((event, index) => (
                    <tr key={event.id} className={index % 2 === 0 ? 'bg-white' : 'bg-bone'}>
                      <td className="px-4 py-3">
                        {new Date(event.date + 'T12:00:00').toLocaleDateString('de-DE', { weekday: 'long' })}
                      </td>
                      <td className="px-4 py-3 font-medium">
                        {new Date(event.date + 'T12:00:00').toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                      </td>
                      <td className="px-4 py-3">{event.time ? `${event.time} Uhr` : '–'}</td>
                      <td className="px-4 py-3">{event.location ? `${event.location} Uhr` : '–'}</td>
                      <td className="px-4 py-3">{event.description || '–'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="mt-8 card-organic p-6 bg-lemon/20">
          <p className="text-text-secondary">
            <strong>Hinweis:</strong> Die Termine können sich bei Bedarf ändern.
            Bitte achte auf aktuelle Aushänge am Vereinshaus.
          </p>
        </div>
      </div>
      <Footer />
    </div>
  );
};

// Sonstige Termine Page
const SonstigeTerminePage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await axios.get(`${API}/events`);
        // Filter for sonstige (skatturnier, stammtisch)
        const sonstigeEvents = response.data.filter(e =>
          e.title.toLowerCase().includes('skat') ||
          e.title.toLowerCase().includes('stammtisch')
        ).sort((a, b) => new Date(a.date) - new Date(b.date));
        setEvents(sonstigeEvents);
      } catch (error) {
        console.error("Error fetching events:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, []);

  return (
    <div className="min-h-screen bg-bone" data-testid="sonstige-page">
      <Navigation activeSection="termine" />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pt-32">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <Link to="/#termine" className="inline-flex items-center gap-2 text-forest hover:text-forest-hover transition-colors print:hidden">
            <ArrowLeft size={18} />
            Zurück zur Übersicht
          </Link>
          <button
            onClick={() => window.print()}
            className="inline-flex items-center gap-2 bg-forest text-white px-4 py-2 rounded-lg hover:bg-forest-hover transition-colors text-sm print:hidden"
            data-testid="print-sonstige-btn"
          >
            <Printer size={16} />
            Drucken / Exportieren
          </button>
        </div>

        <h1 className="font-heading text-4xl md:text-5xl text-forest mb-4">Sonstige Termine 2026</h1>
        <p className="text-text-secondary mb-8">Skatturnier, Stammtisch und weitere Veranstaltungen</p>

        {/* Stammtisch Info */}
        <div className="card-organic p-6 mb-8 border-l-4 border-forest">
          <h2 className="font-heading text-xl text-forest mb-2">Stammtisch</h2>
          <p className="text-text-secondary">
            Jeden zweiten Freitag in der Saison von 17:00 bis ca. 20:00 Uhr.
            Für alle Vereinsmitglieder und deren Gäste, auf nen Klönschnack, bei Bier und Bockwurst.
          </p>
          <p className="text-text-muted mt-2 text-sm flex items-center gap-1">
            <MapPin size={14} />
            Vereinshaus
          </p>
        </div>

        {loading ? (
          <p className="text-text-muted">Termine werden geladen...</p>
        ) : events.length > 0 ? (
          <>
            <h2 className="font-heading text-2xl text-forest mb-6">Skatturnier-Termine</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {events.filter(e => e.title.toLowerCase().includes('skat')).map((event) => (
                <div key={event.id} className="card-organic p-4">
                  <div className="flex items-center gap-4">
                    <div className="bg-lemon text-forest rounded-lg p-3 text-center min-w-[60px]">
                      <div className="text-xl font-bold text-forest">
                        {new Date(event.date).getDate()}
                      </div>
                      <div className="text-xs uppercase text-forest">
                        {new Date(event.date).toLocaleString('de-DE', { month: 'short' })}
                      </div>
                    </div>
                    <div>
                      <h3 className="font-medium text-text-primary">{event.title}</h3>
                      <p className="text-sm text-text-muted flex items-center gap-1">
                        <Clock size={12} />
                        {event.time || "10:00 Uhr"}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <p className="text-text-muted">Keine sonstigen Termine vorhanden.</p>
        )}
      </div>
      <Footer />
    </div>
  );
};

// Admin Page
const AdminPage = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [password, setPassword] = useState('');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [newEvent, setNewEvent] = useState({
    title: '',
    description: '',
    date: '',
    time: '',
    location: 'Vereinshaus',
    event_type: 'general'
  });
  const navigate = useNavigate();

  // Check if already logged in
  useEffect(() => {
    const adminToken = sessionStorage.getItem('adminToken');
    if (adminToken) {
      setIsLoggedIn(true);
      fetchEvents();
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      if (response.data.success) {
        sessionStorage.setItem('adminToken', response.data.token);
        setIsLoggedIn(true);
        fetchEvents();
        toast.success('Erfolgreich angemeldet');
      }
    } catch (error) {
      toast.error('Falsches Passwort');
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('adminToken');
    setIsLoggedIn(false);
    setEvents([]);
    toast.success('Abgemeldet');
  };

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/events`);
      setEvents(response.data.sort((a, b) => new Date(a.date) - new Date(b.date)));
    } catch (error) {
      toast.error('Fehler beim Laden der Termine');
    } finally {
      setLoading(false);
    }
  };

  const handleAddEvent = async (e) => {
    e.preventDefault();
    if (!newEvent.title || !newEvent.date) {
      toast.error('Titel und Datum sind erforderlich');
      return;
    }
    try {
      await axios.post(`${API}/events`, newEvent);
      toast.success('Termin hinzugefügt');
      setNewEvent({
        title: '',
        description: '',
        date: '',
        time: '',
        location: 'Vereinshaus',
        event_type: 'general'
      });
      fetchEvents();
    } catch (error) {
      toast.error('Fehler beim Hinzufügen');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Termin wirklich löschen?')) return;
    try {
      await axios.delete(`${API}/events/${eventId}`);
      toast.success('Termin gelöscht');
      fetchEvents();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const handleUpdateEvent = async (eventId) => {
    try {
      await axios.put(`${API}/events/${eventId}`, editingEvent);
      toast.success('Termin aktualisiert');
      setEditingEvent(null);
      fetchEvents();
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const eventTypes = [
    { value: 'general',    label: 'Allgemein',       color: 'bg-lemon' },
    { value: 'sprechstunde', label: 'Sprechstunde',  color: 'bg-forest' },
    { value: 'fest',       label: 'Fest',            color: 'bg-rose' },
    { value: 'versammlung', label: 'Versammlung',    color: 'bg-earth' },
    { value: 'faekalien',  label: 'Fäkalienabfuhr',  color: 'bg-amber-600' }
  ];

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-bone flex items-center justify-center px-4" data-testid="admin-login">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <img src={LOGO_URL} alt="Logo" className="h-20 w-20 mx-auto mb-4" />
            <h1 className="font-heading text-3xl text-forest">Admin-Bereich</h1>
            <p className="text-text-secondary mt-2">Terminverwaltung</p>
          </div>

          <form onSubmit={handleLogin} className="card-organic p-8">
            <div className="mb-6">
              <label className="block text-sm font-medium text-text-primary mb-2">
                Passwort
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-forest/20 focus:border-forest outline-none"
                placeholder="Admin-Passwort eingeben"
                data-testid="admin-password-input"
              />
            </div>
            <button
              type="submit"
              className="btn-primary w-full flex items-center justify-center gap-2"
              data-testid="admin-login-btn"
            >
              <LogIn size={18} />
              Anmelden
            </button>
          </form>

          <div className="text-center mt-6">
            <Link to="/" className="text-forest hover:text-forest-hover transition-colors text-sm">
              Zurück zur Startseite
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone pt-8 pb-16" data-testid="admin-dashboard">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="Logo" className="h-12 w-12" />
            <div>
              <h1 className="font-heading text-2xl text-forest">Terminverwaltung</h1>
              <p className="text-text-secondary text-sm">Admin-Bereich</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-forest hover:text-forest-hover transition-colors text-sm">
              Zur Website
            </Link>
            <button
              onClick={handleLogout}
              className="btn-secondary flex items-center gap-2 text-sm py-2"
              data-testid="admin-logout-btn"
            >
              <LogOut size={16} />
              Abmelden
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Add Event Form */}
          <div className="lg:col-span-1">
            <div className="card-organic p-6 sticky top-8">
              <h2 className="font-heading text-xl text-forest mb-6 flex items-center gap-2">
                <Plus size={20} />
                Neuer Termin
              </h2>
              <form onSubmit={handleAddEvent} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">
                    Typ
                  </label>
                  <select
                    value={newEvent.event_type}
                    onChange={(e) => {
                      const type = e.target.value;
                      setNewEvent({
                        ...newEvent,
                        event_type: type,
                        title: type === 'faekalien' ? 'Fäkalienabfuhr' : (newEvent.event_type === 'faekalien' ? '' : newEvent.title),
                        location: type === 'faekalien' ? '' : 'Vereinshaus'
                      });
                    }}
                    className="w-full px-3 py-2 rounded-lg border border-forest/20 focus:border-forest outline-none text-sm bg-white"
                    data-testid="new-event-type"
                  >
                    {eventTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">
                    Titel *
                  </label>
                  <input
                    type="text"
                    value={newEvent.title}
                    onChange={(e) => setNewEvent({...newEvent, title: e.target.value})}
                    className="w-full px-3 py-2 rounded-lg border border-forest/20 focus:border-forest outline-none text-sm"
                    placeholder={newEvent.event_type === 'faekalien' ? 'Fäkalienabfuhr' : 'z.B. Vereinsfest'}
                    data-testid="new-event-title"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">
                    Datum *
                  </label>
                  <input
                    type="date"
                    value={newEvent.date}
                    onChange={(e) => setNewEvent({...newEvent, date: e.target.value})}
                    className="w-full px-3 py-2 rounded-lg border border-forest/20 focus:border-forest outline-none text-sm"
                    data-testid="new-event-date"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">
                    {newEvent.event_type === 'faekalien' ? 'Beginn (Uhrzeit)' : 'Uhrzeit'}
                  </label>
                  <input
                    type="text"
                    value={newEvent.time}
                    onChange={(e) => setNewEvent({...newEvent, time: e.target.value})}
                    className="w-full px-3 py-2 rounded-lg border border-forest/20 focus:border-forest outline-none text-sm"
                    placeholder={newEvent.event_type === 'faekalien' ? 'z.B. 08:00' : 'z.B. 10:00 - 14:00 Uhr'}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">
                    {newEvent.event_type === 'faekalien' ? 'Ende (Uhrzeit)' : 'Ort'}
                  </label>
                  <input
                    type="text"
                    value={newEvent.location}
                    onChange={(e) => setNewEvent({...newEvent, location: e.target.value})}
                    className="w-full px-3 py-2 rounded-lg border border-forest/20 focus:border-forest outline-none text-sm"
                    placeholder={newEvent.event_type === 'faekalien' ? 'z.B. 12:00' : 'Vereinshaus'}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">
                    {newEvent.event_type === 'faekalien' ? 'Anzahl Gruben' : 'Beschreibung'}
                  </label>
                  <textarea
                    value={newEvent.description}
                    onChange={(e) => setNewEvent({...newEvent, description: e.target.value})}
                    className="w-full px-3 py-2 rounded-lg border border-forest/20 focus:border-forest outline-none text-sm min-h-[60px] resize-none"
                    placeholder={newEvent.event_type === 'faekalien' ? 'z.B. 10 Gruben' : 'Kurze Beschreibung...'}
                  />
                </div>
                <button
                  type="submit"
                  className="btn-primary w-full text-sm py-2"
                  data-testid="add-event-btn"
                >
                  Termin hinzufügen
                </button>
              </form>
            </div>
          </div>

          {/* Events List */}
          <div className="lg:col-span-2">
            <h2 className="font-heading text-xl text-forest mb-6 flex items-center gap-2">
              <CalendarIcon size={20} />
              Alle Termine ({events.length})
            </h2>

            {loading ? (
              <p className="text-text-muted">Laden...</p>
            ) : events.length === 0 ? (
              <p className="text-text-muted">Keine Termine vorhanden.</p>
            ) : (
              <div className="space-y-4">
                {events.map((event) => (
                  <div
                    key={event.id}
                    className="card-organic p-4"
                    data-testid={`admin-event-${event.id}`}
                  >
                    {editingEvent?.id === event.id ? (
                      // Edit Mode
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs text-text-muted mb-1">Titel</label>
                            <input
                              type="text"
                              value={editingEvent.title}
                              onChange={(e) => setEditingEvent({...editingEvent, title: e.target.value})}
                              className="w-full px-3 py-2 rounded border border-forest/20 text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-text-muted mb-1">Typ</label>
                            <select
                              value={editingEvent.event_type}
                              onChange={(e) => setEditingEvent({...editingEvent, event_type: e.target.value})}
                              className="w-full px-3 py-2 rounded border border-forest/20 text-sm bg-white"
                            >
                              {eventTypes.map(t => (
                                <option key={t.value} value={t.value}>{t.label}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs text-text-muted mb-1">Datum</label>
                            <input
                              type="date"
                              value={editingEvent.date}
                              onChange={(e) => setEditingEvent({...editingEvent, date: e.target.value})}
                              className="px-3 py-2 rounded border border-forest/20 text-sm w-full"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-text-muted mb-1">
                              {editingEvent.event_type === 'faekalien' ? 'Beginn' : 'Uhrzeit'}
                            </label>
                            <input
                              type="text"
                              value={editingEvent.time || ''}
                              onChange={(e) => setEditingEvent({...editingEvent, time: e.target.value})}
                              className="px-3 py-2 rounded border border-forest/20 text-sm w-full"
                              placeholder={editingEvent.event_type === 'faekalien' ? '08:00' : 'Uhrzeit'}
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs text-text-muted mb-1">
                            {editingEvent.event_type === 'faekalien' ? 'Ende (Uhrzeit)' : 'Ort'}
                          </label>
                          <input
                            type="text"
                            value={editingEvent.location || ''}
                            onChange={(e) => setEditingEvent({...editingEvent, location: e.target.value})}
                            className="w-full px-3 py-2 rounded border border-forest/20 text-sm"
                            placeholder={editingEvent.event_type === 'faekalien' ? '12:00' : 'Vereinshaus'}
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-text-muted mb-1">
                            {editingEvent.event_type === 'faekalien' ? 'Anzahl Gruben' : 'Beschreibung'}
                          </label>
                          <textarea
                            value={editingEvent.description}
                            onChange={(e) => setEditingEvent({...editingEvent, description: e.target.value})}
                            className="w-full px-3 py-2 rounded border border-forest/20 text-sm min-h-[60px]"
                            placeholder={editingEvent.event_type === 'faekalien' ? '10 Gruben' : 'Beschreibung...'}
                          />
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleUpdateEvent(event.id)}
                            className="btn-primary text-sm py-2 px-4 flex items-center gap-1"
                          >
                            <Save size={14} />
                            Speichern
                          </button>
                          <button
                            onClick={() => setEditingEvent(null)}
                            className="btn-secondary text-sm py-2 px-4"
                          >
                            Abbrechen
                          </button>
                        </div>
                      </div>
                    ) : (
                      // View Mode
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-4">
                          <div className={`${eventTypes.find(t => t.value === event.event_type)?.color || 'bg-forest'} ${event.event_type === 'faekalien' ? 'text-forest' : 'text-white'} rounded-lg p-3 text-center min-w-[50px]`}>
                            <div className="text-lg font-bold">
                              {new Date(event.date).getDate()}
                            </div>
                            <div className="text-xs uppercase">
                              {new Date(event.date).toLocaleString('de-DE', { month: 'short' })}
                            </div>
                          </div>
                          <div>
                            <h3 className="font-medium text-text-primary">{event.title}</h3>
                            <p className="text-text-secondary text-sm">{event.description}</p>
                            <div className="flex flex-wrap gap-3 mt-2 text-xs text-text-muted">
                              {event.time && (
                                <span className="flex items-center gap-1">
                                  <Clock size={12} />
                                  {event.time}
                                </span>
                              )}
                              {event.location && (
                                <span className="flex items-center gap-1">
                                  <MapPin size={12} />
                                  {event.location}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setEditingEvent(event)}
                            className="p-2 text-forest hover:bg-forest/10 rounded-lg transition-colors"
                            title="Bearbeiten"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={() => handleDeleteEvent(event.id)}
                            className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            title="Löschen"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Home Page Component
const HomePage = () => {
  const [activeSection, setActiveSection] = useState('start');

  useEffect(() => {
    // Intersection Observer for active section
    const sections = document.querySelectorAll('section[id]');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { threshold: 0.3 }
    );

    sections.forEach((section) => observer.observe(section));

    return () => {
      sections.forEach((section) => observer.unobserve(section));
    };
  }, []);

  return (
    <>
      <Navigation activeSection={activeSection} />
      <HeroSection />
      <main id="main-content">
        <StatsSection />
        <VorstandSection />
        <DocumentsSection />
        <MembershipSection />
        <ClubhouseSection />
        <TestimonialsSection />
        <EventsSection />
        <ContactSection />
      </main>
      <Footer />
    </>
  );
};

// Main App Component
function App() {
  return (
    <div className="App">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[100] focus:bg-forest focus:text-white focus:px-4 focus:py-2 focus:rounded-lg focus:text-sm focus:font-medium"
      >
        Zum Hauptinhalt springen
      </a>
      <Toaster position="top-center" richColors />
      <BrowserRouter>
        <CookieBanner />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/impressum" element={<ImpressumPage />} />
          <Route path="/datenschutz" element={<DatenschutzPage />} />
          <Route path="/termine/verein" element={<VereinsTerminePage />} />
          <Route path="/termine/faekalien" element={<FaekalienTerminePage />} />
          <Route path="/termine/sonstige" element={<SonstigeTerminePage />} />
          <Route path="/admin" element={<AdminPage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
